/**
 * CustomerMessageHistory - zeigt die an DIESEN Kunden gesendeten
 * Nachrichten (E-Mail + WhatsApp) im Kundenprofil.
 */
import React from 'react';
import axios from 'axios';
import { Mail, MessageCircle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerMessageHistory = ({ customerId }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/admin/communication-log?customer_id=${customerId}&days=365&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems(Array.isArray(res.data?.items) ? res.data.items : (Array.isArray(res.data) ? res.data : []));
      setLoaded(true);
    } catch (e) {
      setItems([]);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  React.useEffect(() => { load(); }, [load]);

  const fmt = (v) => {
    if (!v) return '';
    try {
      const d = new Date(v);
      return d.toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return String(v).slice(0, 16); }
  };

  return (
    <div className="rounded-lg border p-4 bg-white" data-testid="customer-message-history">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Mail className="h-4 w-4 text-teal-600" /> Nachrichten-Verlauf
        </h3>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1"
          data-testid="message-history-refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </button>
      </div>

      {loading && !loaded && <p className="text-sm text-gray-500">Lade…</p>}
      {loaded && items.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="message-history-empty">
          Noch keine gesendeten Nachrichten (letzte 365 Tage).
        </p>
      )}

      <div className="space-y-2 max-h-80 overflow-y-auto">
        {items.map((m) => (
          <div key={m.id || `${m.channel}-${m.sent_at}`} className="flex items-start gap-3 border-b border-gray-100 pb-2" data-testid="message-history-item">
            <span className={`mt-0.5 shrink-0 ${m.channel === 'whatsapp' ? 'text-green-600' : 'text-blue-600'}`}>
              {m.channel === 'whatsapp' ? <MessageCircle className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-900 truncate">{m.content || m.template_name || '(ohne Betreff)'}</span>
                <span className="text-xs text-gray-400 shrink-0">{fmt(m.sent_at)}</span>
              </div>
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <span className="uppercase">{m.channel}</span>
                {m.trigger && <span>· {m.trigger}</span>}
                {m.status && <span>· {m.status}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerMessageHistory;
